import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  withStyles,
} from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { styles } from '@material-ui/pickers/views/Calendar/Calendar';
import moment from 'moment';
import PropTypes from 'prop-types';

import Checkbox from '~/components/Checkbox';

import { PersonContext } from '../../../contexts/PersonContext';
import { get, patch } from '../../../feathersWrapper';

class EditOrganizationDialog extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { loading: true };
  }

  componentDidMount() {
    this.getData();
  }

  getData = async () => {
    const { login, organizationId } = this.props;

    let value;
    if (organizationId) {
      value = organizationId;
    } else {
      value = login.organizationId;
    }

    const organization = await get(this, 'organizations', value);

    this.setState({
      loading: false,
      organization,
      partnerImportExpenses: organization.partnerImportExpenses,
      bookkeepingStartDate: organization.bookkeepingStartDate,
      partnerImportStartDate: organization.partnerImportStartDate,
    });
  };

  getTrialStatus = () => {
    const { classes } = this.props;
    const { organization, freeTrialExpires } = this.state;

    const datePicker = (
      <KeyboardDatePicker
        className={classes.dateTime}
        label="Change Trial Expiration"
        format="MM/DD/YYYY"
        placeholder="MM/DD/YYYY"
        value={organization.freeTrialExpires}
        onChange={(date) => this.setState({ freeTrialExpires: date })}
        margin="dense"
        fullWidth
        clearable
      />
    );

    if (organization.partnerSubscribed) {
      return <Typography>{`Subscribed via ${organization.partnerName}`}</Typography>;
    }
    if (organization.stripeSubscription) {
      return <Typography>Subscribed</Typography>;
    }

    if (!freeTrialExpires) {
      if (moment().diff(moment(organization.freeTrialExpires).endOf('day')) < 0) {
        return (
          <>
            <Typography>
              {`${moment(organization.freeTrialExpires).diff(moment(), 'days') + 1} days remaining`}
            </Typography>
            {datePicker}
          </>
        );
      }
      if (moment().diff(moment(organization.freeTrialExpires).endOf('day')) >= 0) {
        return (
          <>
            <Typography>Expired</Typography>
            {datePicker}
          </>
        );
      }
    }

    if (freeTrialExpires) {
      return (
        <>
          <Typography color="error">
            {`${moment(freeTrialExpires).diff(moment(), 'days') + 1} days remaining`}
          </Typography>
          {datePicker}
        </>
      );
    }
  };

  updateOrganization = async () => {
    const { closeDialog } = this.props;
    const { freeTrialExpires, organization, partnerImportExpenses, bookkeepingStartDate, partnerImportStartDate } =
      this.state;

    const patchData = {};

    if (freeTrialExpires && freeTrialExpires !== organization.freeTrialExpires) {
      patchData.freeTrialExpires = freeTrialExpires;
    }

    if (partnerImportExpenses !== organization.partnerImportExpenses) {
      patchData.partnerImportExpenses = partnerImportExpenses;
    }

    if (bookkeepingStartDate !== organization.bookkeepingStartDate) {
      patchData.bookkeepingStartDate = bookkeepingStartDate ? moment(bookkeepingStartDate).format('YYYY-MM-DD') : null;
    }

    if (partnerImportStartDate !== organization.partnerImportStartDate) {
      patchData.partnerImportStartDate = partnerImportStartDate
        ? moment(partnerImportStartDate).format('YYYY-MM-DD')
        : null;
    }

    if (Object.keys(patchData).length > 0) {
      await patch(this, 'organizations', organization.id, patchData);
    }

    closeDialog();
  };

  render() {
    const { loading, partnerImportExpenses, bookkeepingStartDate, partnerImportStartDate, organization } = this.state;
    const { isOpen, closeDialog } = this.props;

    if (loading) {
      return null;
    }

    return (
      <Dialog open={isOpen} onClose={closeDialog} aria-labelledby="form-dialog-title" fullWidth>
        <DialogTitle id="form-dialog-title">Edit Organization</DialogTitle>
        <DialogContent>
          <Box mb={2}>{this.getTrialStatus()}</Box>
          <Box mb={2}>
            <KeyboardDatePicker
              label="Bookkeeping Start Date"
              format="MM/DD/YYYY"
              placeholder="MM/DD/YYYY"
              value={bookkeepingStartDate}
              onChange={(date) => this.setState({ bookkeepingStartDate: date })}
              margin="dense"
              fullWidth
              clearable
            />
            <Typography variant="caption">
              Controls how far back bank transactions are imported and sets the default date for opening balances.
            </Typography>
          </Box>
          {organization.partnerName === 'TurboTenant' && (
            <Box mb={2}>
              <KeyboardDatePicker
                label={`${organization.partnerName} Import Start Date`}
                format="MM/DD/YYYY"
                placeholder="MM/DD/YYYY"
                value={partnerImportStartDate}
                onChange={(date) => this.setState({ partnerImportStartDate: date })}
                margin="dense"
                fullWidth
                clearable
              />
              <Typography variant="caption">
                Partner transactions dated before this date will be skipped during import. Leave empty to import all.
              </Typography>
            </Box>
          )}
          {organization.partnerName === 'TurboTenant' && (
            <>
              <Checkbox
                checked={partnerImportExpenses}
                onChange={() => this.setState({ partnerImportExpenses: !partnerImportExpenses })}
                name="partnerImportExpenses"
                label="Import Partner Expenses"
              />
              <Typography variant="caption">
                Setting this value to true will include TurboTenant expenses in the user’s portfolio during a re-sync.
                Setting this value to false will exclude TurboTenant expenses from being synced during a portfolio
                re-sync.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              this.updateOrganization();
            }}
            color="primary"
            variant="contained"
            disableElevation
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

EditOrganizationDialog.contextType = PersonContext;

EditOrganizationDialog.defaultProps = {
  organizationId: '',
};

EditOrganizationDialog.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  isOpen: PropTypes.bool.isRequired,
  closeDialog: PropTypes.func.isRequired,
  login: PropTypes.object.isRequired,
  organizationId: PropTypes.string,
};

export default withStyles(styles)(EditOrganizationDialog);
